/**
 * Centralt videobibliotek för Video-SEO.
 *
 * Varje YouTube-film som visas någonstans på sajten ska ha en post här.
 * Posten driver tre saker samtidigt:
 *   1. VideoObject-schema på varje sida där filmen är inbäddad
 *      (<VideoSchema />), så filmerna kan plockas upp av Googles
 *      videokarusell och Video-fliken.
 *   2. Video-sitemap (/sitemap-video.xml).
 *   3. Kapitelmarkup (Clip) och transcript när sådan finns.
 *
 * TRANSCRIPT: fyll `transcript` med verkligt tal från filmen. Vi hittar
 * ALDRIG på transcript-text — saknas den utelämnas propertyn helt.
 * Detsamma gäller `chapters` (Clip) och `duration`.
 */

export type VideoChapter = {
  name: string;
  /** Sekunder in i filmen där kapitlet börjar. */
  startOffsetSec: number;
  /** Sekunder in i filmen där kapitlet slutar (valfritt men rekommenderat). */
  endOffsetSec?: number;
};

export type VideoMeta = {
  /** YouTube-ID. */
  id: string;
  name: string;
  description: string;
  /** ISO-datum (YYYY-MM-DD) för publicering. */
  uploadDate: string;
  /** ISO 8601-duration, t.ex. "PT1M12S". Utelämnas om okänd. */
  duration?: string;
  client?: string;
  /** Verkligt transcript. Lämna tomt tills riktig text finns. */
  transcript?: string;
  /** Verkliga kapitel. Lämna tomt tills riktiga tider finns. */
  chapters?: VideoChapter[];
  /** Relativ sökväg till den sida som är filmens "hem" (case eller tjänst). */
  primaryPath?: string;
};

const V = (v: VideoMeta) => v;

export const videoLibrary: VideoMeta[] = [
  V({
    id: "9iiyNS4yNzA",
    name: "Maria på Garageportexperten — employer branding-film",
    description:
      "Employer branding-film där servicetekniker Maria berättar varför hon stannat i elva år. Producerad av Fenny för Garageportexperten: story, regi, foto, klipp och sex sociala versioner från samma inspelning.",
    uploadDate: "2026-04-16",
    duration: "PT27S",
    client: "Garageportexperten",
    primaryPath: "/case/garageportexperten-maria",
  }),
  V({
    id: "KkE8BqgQ14M",
    name: "30 år med Garageportexperten — jubileumsfilm",
    description:
      "Jubileumsfilm som sammanfattar 30 år av Garageportexperten. Visad på event, sajt och sociala kanaler. Produktion: Fenny — koncept, regi, foto, klipp och ljudläggning.",
    uploadDate: "2025-04-07",
    duration: "PT6M50S",
    client: "Garageportexperten",
    primaryPath: "/case/garageportexperten-30-ar",
  }),
  V({
    id: "UU_Ys1M431I",
    name: "Årets Pryl 2026 — kampanjfilm",
    description:
      "Kampanjfilm för Årets Pryl 2026 med Elektronikbranschen. Snabbt tempo anpassat för säsongens trafiktopp, producerad av Fenny i Stockholm.",
    uploadDate: "2026-04-16",
    duration: "PT33S",
    client: "Elektronikbranschen",
    primaryPath: "/case/arets-pryl-2026",
  }),
  V({
    id: "HwWGVOl6dtg",
    name: "Huawei Energy Solution in Denmark — B2B-brandfilm",
    description:
      "B2B-brandfilm för ALSO och Huawei, inspelad i Danmark och levererad i flera språkversioner från en och samma produktion. Producerad av Fenny.",
    uploadDate: "2026-04-16",
    duration: "PT2M4S",
    client: "ALSO / Huawei",
    primaryPath: "/case/huawei-also-energy",
  }),
  V({
    id: "yvojXrb41Aw",
    name: "Bolt — 20 sekunders reklamfilm för paid social",
    description:
      "Kort reklamfilm för Bolt byggd för paid social: hook inom tre sekunder och tydlig uppmaning på tjugo. Producerad av Fenny.",
    uploadDate: "2025-08-13",
    duration: "PT20S",
    client: "Bolt",
    primaryPath: "/case/bolt-20-sekunder",
  }),
  V({
    id: "WONrMkeJuik",
    name: "Wimeck Detailing — Allt på en plats",
    description:
      "Varumärkes- och säljfilm för Wimeck Detailing som kombinerar känsla och konkret erbjudande i samma klipp. Produktion: Fenny.",
    uploadDate: "2026-03-26",
    duration: "PT31S",
    client: "Wimeck",
    primaryPath: "/case/wimeck-detailing",
  }),
  V({
    id: "O2zdqLfmM5E",
    name: "Garageportexperten Hemma — kampanjfilm",
    description:
      "Del av ett långsiktigt kampanjspår där varje månads film bygger vidare på föregående. Producerad av Fenny för Garageportexperten.",
    uploadDate: "2026-04-16",
    duration: "PT11S",
    client: "Garageportexperten",
    primaryPath: "/case/garageportexperten-hemma",
  }),
  V({
    id: "GPm8ubB79DM",
    name: "Årets Pryl 2026 — eventfilm",
    description:
      "Eventfilm från Årets Pryl 2026 för Elektronikbranschen. Fenny stod för foto, ljud, klipp och färgsättning.",
    uploadDate: "2025-11-14",
    duration: "PT1M18S",
    client: "Elektronikbranschen",
  }),
  V({
    id: "_b35xC0m1Ko",
    name: "Årets Pryl 2026 — motion graphics",
    description:
      "Grafikdriven filmsekvens för Årets Pryl 2026. Motion design och animation av Fenny.",
    uploadDate: "2025-11-12",
    duration: "PT1M10S",
    client: "Elektronikbranschen",
  }),
  V({
    id: "BzHML38R2bs",
    name: "Pricka — reklamfilm",
    description:
      "Reklamfilm för Pricka med fokus på stoppkraft i flödet. Koncept, produktion och efterarbete av Fenny.",
    uploadDate: "2025-06-23",
    duration: "PT1M",
    client: "Pricka",
  }),
  V({
    id: "oXoAyTogRrA",
    name: "Svensk Radonförening — poddproduktion",
    description:
      "Videopodd för Svensk Radonförening: flerkameraupptagning, ljudmix och klipp för både video- och ljudkanaler. Produktion: Fenny.",
    uploadDate: "2025-11-06",
    duration: "PT27M55S",
    client: "Svensk Radonförening",
  }),
  V({
    id: "Rq9Ghsq1Lmo",
    name: "Företagsfilm av Fenny — reel",
    description: "Produktionsexempel ur Fennys arbetsreel: företagsfilm producerad i Stockholm.",
    uploadDate: "2024-11-15",
    duration: "PT8S",
  }),
  V({
    id: "Odua3179y5s",
    name: "Reklamfilm av Fenny — reel",
    description: "Produktionsexempel ur Fennys arbetsreel: reklamfilm producerad i Stockholm.",
    uploadDate: "2025-06-13",
    duration: "PT18S",
  }),
  V({
    id: "UxwHfbwlTYI",
    name: "Varumärkesfilm av Fenny — reel",
    description: "Produktionsexempel ur Fennys arbetsreel: varumärkesfilm producerad i Stockholm.",
    uploadDate: "2025-06-19",
    duration: "PT34S",
  }),
  V({
    id: "2YCWQZnks5Q",
    name: "Kampanjfilm av Fenny — reel",
    description: "Produktionsexempel ur Fennys arbetsreel: kampanjfilm producerad i Stockholm.",
    uploadDate: "2025-06-23",
    duration: "PT2M47S",
  }),
  V({
    id: "lEr1THX_XzE",
    name: "Produktfilm av Fenny — reel",
    description: "Produktionsexempel ur Fennys arbetsreel: produktfilm producerad i Stockholm.",
    uploadDate: "2025-06-23",
    duration: "PT49S",
  }),
  V({
    id: "8cq2SeH1AGQ",
    name: "Eventfilm av Fenny — reel",
    description: "Produktionsexempel ur Fennys arbetsreel: eventfilm producerad i Stockholm.",
    uploadDate: "2025-08-13",
    duration: "PT6S",
  }),
  V({
    id: "HqMpIkQX5kg",
    name: "Dokumentärfilm av Fenny — reel",
    description: "Produktionsexempel ur Fennys arbetsreel: dokumentär filmproduktion i Stockholm.",
    uploadDate: "2025-09-01",
    duration: "PT1M2S",
  }),
  V({
    id: "1iNffzbqLmU",
    name: "Intervjufilm av Fenny — reel",
    description: "Produktionsexempel ur Fennys arbetsreel: intervju- och medarbetarfilm.",
    uploadDate: "2025-09-02",
    duration: "PT55S",
  }),
  V({
    id: "ePVXNuo62Jc",
    name: "Rekryteringsfilm av Fenny — reel",
    description: "Produktionsexempel ur Fennys arbetsreel: rekryterings- och employer branding-film.",
    uploadDate: "2025-10-07",
    duration: "PT9S",
  }),
  V({
    id: "q48ND68nPu8",
    name: "Sociala klipp av Fenny — reel",
    description: "Produktionsexempel ur Fennys arbetsreel: korta klipp för sociala kanaler.",
    uploadDate: "2025-11-11",
    duration: "PT38S",
  }),
  V({
    id: "V4yA-e26XBc",
    name: "Filmproduktion av Fenny — reel",
    description: "Produktionsexempel ur Fennys arbetsreel: filmproduktion i Stockholm.",
    uploadDate: "2025-12-29",
    duration: "PT15S",
  }),
];

const byId = new Map(videoLibrary.map((v) => [v.id, v]));

/** Slår upp en film; okända ID:n får en rimlig fallback så markup aldrig saknas. */
export function getVideoMeta(id: string, fallbackTitle?: string): VideoMeta {
  const hit = byId.get(id);
  if (hit) return hit;
  return {
    id,
    name: fallbackTitle?.trim() || "Filmproduktion av Fenny",
    description:
      "Film producerad av Fenny — filmproduktionsbyrå i Stockholm. Koncept, regi, foto, klipp och färgsättning.",
    uploadDate: "2024-03-01",
  };
}

export const YT_THUMB = (id: string) => `https://i.ytimg.com/vi/${id}/maxresdefault.jpg`;
export const YT_EMBED = (id: string) => `https://www.youtube.com/embed/${id}`;
export const YT_WATCH = (id: string) => `https://www.youtube.com/watch?v=${id}`;
